import React, { useState, useRef, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import RestoreIcon from '@material-ui/icons/Restore';
import { useOrganizationStore } from '../../store/organization.store';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    color: '#fff',
    padding: 4,
    marginRight: 2,
  },
  popover: {
    backgroundColor: 'transparent',
    backdropFilter: 'blur(10px)',
    borderRadius: 8,
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '0 0 16px 0',
    minWidth: 300,
  },
  autocomplete: {
    width: 300,
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      color: '#fff',
      minHeight: '32px',
      fontFamily: 'var(--font-family-primary)',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
        borderWidth: '1px'
      }
    },
    '& input': {
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)',
      fontFamily: 'var(--font-family-primary)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
      background: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    },
    '& .MuiAutocomplete-popper': {
      '& .MuiPaper-root': {
        color: '#fff',
      },
      '& .MuiAutocomplete-option': {
        fontSize: 'var(--menu-font-size)',
        fontFamily: 'var(--font-family-primary)',
        padding: '8px 16px',
        '&[data-focus="true"]': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        },
        '&[aria-selected="true"]': {
          backgroundColor: 'var(--accent-1-color)'
        }
      }
    }
  },
}));

const ColumnVisibilitySelector = ({
  columnFilterChanged,
  isOpen,
  setIsOpen,
  selectedColumns,
  setSelectedColumns,
  onSave,
  onRecall,
  mode
}) => {
  const classes = useStyles();
  // Get org-level role from organization store
  // Public mode always uses USER-level column restrictions
  const currentOrgRole = useOrganizationStore((state) => state.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((state) => state.isSuperAdmin);
  const role = mode === 'public' ? 'USER' : (isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER'));
  const inputRef = useRef(null);
  const [iconAnchorEl, setIconAnchorEl] = useState(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Column options based on role
  const columnOptions = useMemo(() => {
    const options = [
      { id: 'trackName', label: 'Track Name' },
      { id: 'length', label: 'Length' },
      ...(['ADMIN', 'OPERATOR'].includes(role) ? [
        { id: 'licenseStatus', label: 'License Status' },
        { id: 'isPublic', label: 'Public' },
        { id: 'composers', label: 'Composers' },
      ] : []),
      { id: 'genre', label: 'Genre' },
      { id: 'bpm', label: 'BPM' },
      { id: 'key', label: 'Key' },
      { id: 'tags', label: 'Tags' },
      ...(['ADMIN', 'OPERATOR'].includes(role) ? [
        { id: 'trackRating', label: 'Track Rating' },
      ] : []),
      { id: 'audioFiles', label: 'Audio Files' },
      ...(['ADMIN', 'OPERATOR'].includes(role) ? [
        { id: 'flag', label: 'Flag' },
      ] : []),
      ...(['ADMIN', 'OPERATOR'].includes(role) ? [
        { id: 'jobTitle', label: 'Job Title' },
        { id: 'producer', label: 'Producer' },
        { id: 'artist', label: 'Artist' },
        { id: 'album', label: 'Album' },
        { id: 'recordLabel', label: 'Record Label' },
        { id: 'publisher', label: 'Publisher' },
        { id: 'agency', label: 'Agency' },
        { id: 'client', label: 'Client' },
        { id: 'creativeDirector', label: 'Creative Director' },
        { id: 'dateCompleted', label: 'Date Completed' },
        { id: 'dateExpires', label: 'Date Expires' },
        { id: 'flightingPlatform', label: 'Flighting Platform' },
        { id: 'flightingCode', label: 'Flighting Code' },
        { id: 'studio', label: 'Studio' },
        { id: 'backupLocation', label: 'Backup Location' },
        { id: 'sessionArtists', label: 'Session Artists' },
      ] : [])
    ];
    
    // Sort alphabetically by label
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [role]);

  // Popover open state
  const open = Boolean(iconAnchorEl) && isOpen;
  const id = open ? 'column-visibility-popover' : undefined;

  // Handle icon button click
  const handleClick = (event) => {
    setIconAnchorEl(event.currentTarget);
    setIsOpen(true);
    setAutocompleteOpen(true);
  };

  // Handle popover close
  const handleClose = () => {
    setIconAnchorEl(null);
    setIsOpen(false);
    setAutocompleteOpen(false);
  };

  // Handle column change
  const handleColumnChange = (event, newValue) => {
    setSelectedColumns(newValue.map(opt => opt.id));
  };

  // Focus input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        className={classes.iconButton}
        size="small"
      >
        <ViewColumnIcon fontSize="small" />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={iconAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          className: classes.popover,
        }}
        className="column-visibility-popover"
        transitionDuration={0}
        onMouseDown={e => e.stopPropagation()}
      >
        <Autocomplete
          multiple
          options={columnOptions}
          getOptionLabel={(option) => option.label}
          value={columnOptions.filter(opt => selectedColumns.includes(opt.id))}
          onChange={handleColumnChange}
          className="column-visibility-autocomplete"
          onMouseDown={e => e.stopPropagation()}
          popupIcon={null}
          renderInput={(params) => {
            const { endAdornment, ...inputPropsRest } = params.InputProps;
            return (
              <TextField
                {...params}
                inputRef={el => {
                  inputRef.current = el;
                  if (el && !autocompleteOpen) setAutocompleteOpen(true);
                }}
                variant="outlined"
                placeholder="Type to filter columns"
                className={classes.autocomplete}
                autoFocus
                InputProps={{
                  ...inputPropsRest,
                  endAdornment: (
                    <>
                      <IconButton
                        size="small"
                        aria-label="Save column selection"
                        style={{ color: '#fff' }}
                        tabIndex={-1}
                        onClick={onSave}
                      >
                        <SaveAltIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Recall column selection"
                        style={{ color: '#fff' }}
                        tabIndex={-1}
                        onClick={onRecall}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }}
              />
            );
          }}
          renderTags={() => null}
          ListboxProps={{
            style: {
              fontSize: 'var(--menu-font-size)',
            },
          }}
          renderOption={(option, { selected, focused }) => (
            <div
              style={{
                backgroundColor: selected
                  ? 'var(--accent-1-color)'
                  : focused
                    ? 'rgba(255,255,255,0.1)'
                    : 'transparent',
                color: '#fff',
                cursor: 'pointer',
                borderRadius: selected ? '8px' : undefined,
                padding: '4px 16px',
                margin: selected ? '2px 0' : '0',
                display: 'inline-block',
                minWidth: '80%',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {option.label}
            </div>
          )}
          disableClearable
          open={autocompleteOpen}
          autoHighlight
        />
      </Popover>
    </>
  );
};

export default ColumnVisibilitySelector; 